import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { CapabilityMode } from "@/api/admin/delegation"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useAdminCustomerPreviewStore } from "@/stores/admin-customer-preview-store"

import {
  useDelegationMatrixQuery,
  useUpdateDelegationCapabilityMutation,
} from "../hooks/admin-queries"

type DelegationModeControlProps = {
  /** Key in core/admin_capabilities.py — must match the section's mapping in management-section-registry.ts. */
  capability: string
}

/**
 * Inline hidden/available/manage selector for a single delegable capability,
 * embedded directly on that capability's own Management-tab section (see
 * management-section-registry.ts's `capability` field for how sections map
 * to keys). Platform-admin only, and hidden while the customer-preview
 * toggle is on — editing has no place inside a "what would a customer see"
 * preview of the same account.
 */
export function DelegationModeControl({ capability }: DelegationModeControlProps) {
  const { t } = useTranslation()
  const { data: config } = useConfigQuery()
  const isPlatformAdmin = config?.user_policy.is_platform_admin ?? false
  const previewEnabled = useAdminCustomerPreviewStore((s) => s.enabled)
  const {
    data: delegationData,
    isLoading: isDelegationLoading,
    isError: isDelegationError,
  } = useDelegationMatrixQuery(isPlatformAdmin)
  const mutation = useUpdateDelegationCapabilityMutation()

  if (!isPlatformAdmin || previewEnabled) return null

  const mode: CapabilityMode = delegationData?.capabilities[capability] ?? "hidden"
  // isError alone would stay disabled forever after a later background
  // refetch fails (e.g. invalidateQueries following a successful save) even
  // though the cache already holds a trustworthy value — React Query keeps
  // the last good `data` around across a failed refetch. Only the initial,
  // never-yet-loaded case (no delegationData at all) needs to block editing
  // against the defaulted "hidden" placeholder.
  const isUntrustworthy = isDelegationError && !delegationData

  const handleChange = (nextMode: CapabilityMode) => {
    mutation.mutate(
      { capability, mode: nextMode },
      {
        onSuccess: () => {
          toast.success(
            t("adminDashboard.management.delegation.updated", {
              defaultValue: "Customer admin access updated",
            })
          )
        },
        onError: () => {
          toast.error(
            t("adminDashboard.management.delegation.updateFailed", {
              defaultValue: "Failed to update customer admin access",
            })
          )
        },
      }
    )
  }

  return (
    <Select
      disabled={mutation.isPending || isDelegationLoading || isUntrustworthy}
      value={mode}
      onValueChange={(value) => handleChange(value as CapabilityMode)}
    >
      <SelectTrigger
        className="h-8 w-36 text-xs"
        aria-label={t("adminDashboard.management.delegation.label", {
          defaultValue: "Customer admin access",
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="hidden">
          {t("adminDashboard.management.delegation.modeHidden", { defaultValue: "Hidden" })}
        </SelectItem>
        <SelectItem value="available">
          {t("adminDashboard.management.delegation.modeAvailable", { defaultValue: "Available" })}
        </SelectItem>
        <SelectItem value="manage">
          {t("adminDashboard.management.delegation.modeManage", { defaultValue: "Manage" })}
        </SelectItem>
      </SelectContent>
    </Select>
  )
}
